import * as vp from 'vopay';
import { FunctionComponent, JSX, useEffect } from 'react';
import { useClientData } from '../../../../providers/ClientData.provider';
import styles from '../ScheduledTransactions.module.scss';
import ApprovalPanel from '../../../UI/ApprovalPanel/ApprovalPanel';
import { useApprovalWorkflow } from '../../../../providers/ApprovalWorkflow.provider';
import { useTranslation } from 'react-i18next';

const { Box, SubTitle, Table, Badge, formattedCurrency } = vp;

type TablePropsHandlers = {
  scheduleDetails: () => vp.TableProps;
  accountHolderDetails: () => vp.TableProps;
  bankAccountDetails: () => vp.TableProps;
};

type KeyValuePair = {
  display: string;
  value: string | JSX.Element;
  visible?: boolean;
};

type TransactionOverviewProps = {
  transaction: vp.ScheduledTransactionsList;
};

const ScheduledTransactionOverview: FunctionComponent<TransactionOverviewProps> = ({ transaction }) => {
  const { response } = useClientData();
  const { transactionsReset, approvalListReset } = useApprovalWorkflow();
  const { t } = useTranslation('translations', { keyPrefix: 'ScheduledTransactions.Details' });

  useEffect(() => {
    if (transaction) {
      if (transaction.Status === 'pending approval') {
        transactionsReset();
        approvalListReset();
      }
    }
  }, [transaction]);

  const errorMessage = Object.entries(transaction.ScheduledDetails.ErrorMessage || {});

  const statusLabels: Record<vp.AccountTransactionStatus, string> = {
    'in progress': 'inProgress',
    'pending approval': 'pendingApproval',
    pending: 'pending',
    requested: 'requested',
    sent: 'sent',
    successful: 'successful',
    failed: 'failed',
    cancelled: 'cancelled',
    'cancellation requested': 'cancellationRequested',
    settled: 'settled',
    waiting: 'waiting',
    declined: 'declined',
    completed: 'completed',
    'pending manual review': 'pendingManualReview',
    received: 'received',
    'request fulfilled': 'requestFulfilled',
    delayed: 'delayed',
    'submission failed': 'submissionFailed',
    'pending confirmation': 'pendingConfirmation',
  };

  return (
    <Box flx="col" width="50%" mt="md">
      {transaction.Status === 'pending approval' && (
        <Box pb="lg" mb="sm">
          <ApprovalPanel
            title="Status: Pending Approval"
            description="Approvers can find these transaction(s) in their Approval Inbox"
          />
        </Box>
      )}
      <SubTitle subTitle={t('ScheduleDetailsSection.title')} />
      <Box mb="lg">
        <div className={styles.tableContainer}>
          <Table {...getTableProps('scheduleDetails')} />
        </div>
      </Box>
      <SubTitle subTitle={t('AccountHolderDetailsSection.title')} />
      <Box mb="lg">
        <div className={styles.tableContainer}>
          <Table {...getTableProps('accountHolderDetails')} />
        </div>
      </Box>
      <SubTitle subTitle={t('BankAccountDetailsSection.title')} />
      <Box mb="lg">
        <div className={styles.tableContainer}>
          <Table {...getTableProps('bankAccountDetails')} />
        </div>
      </Box>
      {errorMessage.length > 0 && (
        <>
          <SubTitle subTitle={t('FailedScheduledTransactionsSection.title')} />
          <Box mb="lg">
            <div className={styles.tableContainer}>
              <Table
                {...{
                  name: 'Errors-details',
                  variant: 'pair',
                  columns: {
                    detailKey: { header: 'Errors' },
                    detailValue: { header: '' },
                  },
                  rows: errorMessage.map((value) => ({
                    detailKey: value[0],
                    detailValue: value[1].ErrorMessage,
                  })),
                }}
              />
            </div>
          </Box>
        </>
      )}
    </Box>
  );

  function getTableProps(handler: keyof TablePropsHandlers): vp.TableProps {
    const handlers: TablePropsHandlers = {
      scheduleDetails: () => {
        const accountDetailsPair: KeyValuePair[] = [
          {
            display: t('ScheduleDetailsSection.Rows.scheduleID'),
            value: transaction.ID || '',
            visible: true,
          },
          {
            display: t('ScheduleDetailsSection.Rows.Status.label'),
            value: (
              <Badge
                label={t(`ScheduleDetailsSection.Rows.Status.Options.${statusLabels[transaction.Status]}`, '')}
                hasIcon
                color={vp.GetColorStatus(transaction.Status || '')}
              />
            ),
            visible: true,
          },
          {
            display: t('ScheduleDetailsSection.Rows.ScheduleType.label'),
            value: t(`ScheduleDetailsSection.Rows.ScheduleType.Options.${transaction.Frequency}`, ''),
            visible: true,
          },
          {
            display: t('ScheduleDetailsSection.Rows.Frequency.label'),
            value: transaction.NameOfFrequency
              ? t(`ScheduleDetailsSection.Rows.Frequency.Options.${transaction.NameOfFrequency}`, '')
              : '',
            visible: transaction.NameOfFrequency !== '',
          },
          {
            display: t('ScheduleDetailsSection.Rows.numberOfPayments'),
            value: transaction.EndingAfterPayments || '',
            visible: transaction.EndingAfterPayments !== '',
          },
          {
            display: t('ScheduleDetailsSection.Rows.description'),
            value: transaction.Description || '',
            visible: transaction.Description !== '',
          },
          {
            display: t('ScheduleDetailsSection.Rows.type'),
            value: transaction.Type || '',
            visible: true,
          },
          {
            display: t('ScheduleDetailsSection.Rows.amount'),
            value: `$${formattedCurrency(transaction.Amount || '')}`,
            visible: true,
          },
          {
            display: t('ScheduleDetailsSection.Rows.currency'),
            value: response.Country === 'CA' ? 'CAD' : 'USD',
            visible: true,
          },
        ];

        const scheduleDetails: vp.TableProps = {
          name: 'scheduled-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('ScheduleDetailsSection.title') },
            detailValue: { header: '' },
          },
          rows: accountDetailsPair
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return scheduleDetails;
      },
      accountHolderDetails: () => {
        const clientDetailsPair: KeyValuePair[] = [
          {
            display: t('AccountHolderDetailsSection.Rows.name'),
            value: transaction.ScheduledDetails.CompanyName
              ? transaction.ScheduledDetails.CompanyName
              : `${transaction.ScheduledDetails.FirstName || ''} ${transaction.ScheduledDetails.LastName || ''} `,
            visible: true,
          },
          {
            display: t('AccountHolderDetailsSection.Rows.address'),
            value: transaction.ScheduledDetails.Address1 || '',
            visible: transaction.ScheduledDetails.Address1 !== '',
          },
          {
            display: t('AccountHolderDetailsSection.Rows.city'),
            value: transaction.ScheduledDetails.City || '',
            visible: transaction.ScheduledDetails.City !== '',
          },
          {
            display: t('AccountHolderDetailsSection.Rows.provinceState'),
            value: transaction.ScheduledDetails.Province || '',
            visible: transaction.ScheduledDetails.Province !== '',
          },
          {
            display: t('AccountHolderDetailsSection.Rows.country'),
            value: transaction.ScheduledDetails.Country || 'Canada',
            visible: true,
          },
          {
            display: t('AccountHolderDetailsSection.Rows.postalCodeZipCode'),
            value: transaction.ScheduledDetails.PostalCode || '',
            visible: transaction.ScheduledDetails.PostalCode !== '',
          },
          {
            display: t('AccountHolderDetailsSection.Rows.primaryContact'),
            value: transaction.ScheduledDetails.PhoneNumber || '',
            visible: transaction.ScheduledDetails.PhoneNumber !== '',
          },
        ];

        const accountHolderDetails: vp.TableProps = {
          name: 'account-holder-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('AccountHolderDetailsSection.title') },
            detailValue: { header: '' },
          },
          rows: clientDetailsPair
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return accountHolderDetails;
      },
      bankAccountDetails: () => {
        const clientDetailsPair: KeyValuePair[] = [
          {
            display: t('BankAccountDetailsSection.Rows.financialInstitutionNumber'),
            value: transaction.ScheduledDetails.FinancialInstitutionNumber || '',
            visible: transaction.ScheduledDetails.FinancialInstitutionNumber !== '',
          },
          {
            display: t('BankAccountDetailsSection.Rows.branchTransitNumber'),
            value: transaction.ScheduledDetails.BranchTransitNumber || '',
            visible: transaction.ScheduledDetails.BranchTransitNumber !== '',
          },
          {
            display: t('BankAccountDetailsSection.Rows.accountNumber'),
            value: transaction.ScheduledDetails.AccountNumber || '',
            visible: transaction.ScheduledDetails.AccountNumber !== '',
          },
        ];

        const BankAccountDetails: vp.TableProps = {
          name: 'bank-account-details',
          variant: 'pair',
          columns: {
            detailKey: { header: t('BankAccountDetailsSection.title') },
            detailValue: { header: '' },
          },
          rows: clientDetailsPair
            .filter(({ visible }) => visible)
            .map(({ display, value }) => ({
              detailKey: display,
              detailValue: value,
            })),
        };

        return BankAccountDetails;
      },
    };

    return handlers[handler]();
  }
};

export default ScheduledTransactionOverview;
